import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, ChevronDown, Info, X } from 'lucide-react';

const FlowChart = ({ flowchart }) => {
  const [selectedNode, setSelectedNode] = useState(null);

  if (!flowchart || !flowchart.steps || flowchart.steps.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Flow chart not available</p>
      </div>
    );
  }

  const steps = flowchart.steps;

  // Organize steps into main flow and side branches
  const mainFlow = [];
  const sideBranches = {};
  
  steps.forEach((step, index) => {
    if (step.branch_target !== undefined && step.branch_target !== null) {
      // This is a side branch
      if (!sideBranches[step.branch_target]) {
        sideBranches[step.branch_target] = [];
      }
      sideBranches[step.branch_target].push({ ...step, originalIndex: index });
    } else {
      mainFlow.push({ ...step, originalIndex: index });
    }
  });

  // If no explicit branches, treat all as main flow
  const displaySteps = mainFlow.length > 0 ? mainFlow : steps.map((s, i) => ({ ...s, originalIndex: i }));

  const getNodeColor = (type) => {
    const colors = {
      lab: { bg: 'bg-teal-500/10', border: 'border-teal-500/40', text: 'text-teal-400', glow: 'shadow-teal-500/20' },
      computational: { bg: 'bg-violet-500/10', border: 'border-violet-500/40', text: 'text-violet-400', glow: 'shadow-violet-500/20' },
      analysis: { bg: 'bg-amber-500/10', border: 'border-amber-500/40', text: 'text-amber-400', glow: 'shadow-amber-500/20' },
      review: { bg: 'bg-blue-500/10', border: 'border-blue-500/40', text: 'text-blue-400', glow: 'shadow-blue-500/20' },
      hypothesis: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/40', text: 'text-yellow-400', glow: 'shadow-yellow-500/20' },
      results: { bg: 'bg-yellow-200/15', border: 'border-yellow-300/50', text: 'text-yellow-300', glow: 'shadow-yellow-300/20' },
      discussion: { bg: 'bg-rose-500/10', border: 'border-rose-500/40', text: 'text-rose-400', glow: 'shadow-rose-500/20' },
      method: { bg: 'bg-teal-500/10', border: 'border-teal-500/40', text: 'text-teal-400', glow: 'shadow-teal-500/20' },
      limitation: { bg: 'bg-orange-500/10', border: 'border-orange-500/40', text: 'text-orange-400', glow: 'shadow-orange-500/20' },
      result: { bg: 'bg-yellow-200/15', border: 'border-yellow-300/50', text: 'text-yellow-300', glow: 'shadow-yellow-300/20' },
    };
    return colors[type] || colors.method;
  };

  const FlowNode = ({ step, index, isLast, hasSideBranch }) => {
    const color = getNodeColor(step.type);
    const isSelected = selectedNode === step.originalIndex;
    const branch = sideBranches[index];

    return (
      <div className="relative">
        {/* Main Node Row */}
        <div className="flex items-center justify-center gap-4">
          {/* Left Side Branch */}
          {branch && branch.some(b => b.side === 'left') && (
            <div className="flex items-center">
              {branch.filter(b => b.side === 'left').map((branchStep, bi) => (
                <motion.div
                  key={bi}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center"
                >
                  <div 
                    onClick={() => setSelectedNode(isSelected && selectedNode === branchStep.originalIndex ? null : branchStep.originalIndex)}
                    className={`
                      px-4 py-3 rounded-xl border cursor-pointer transition-all duration-300
                      ${getNodeColor(branchStep.type).bg} ${getNodeColor(branchStep.type).border}
                      hover:shadow-lg ${getNodeColor(branchStep.type).glow}
                      ${selectedNode === branchStep.originalIndex ? `ring-2 ring-offset-2 ring-offset-slate-900 ${getNodeColor(branchStep.type).border}` : ''}
                      max-w-[180px]
                    `}
                  >
                    <p className="text-sm text-slate-200 text-center leading-tight">{branchStep.label}</p>
                  </div>
                  {/* Arrow pointing right */}
                  <svg width="40" height="24" className="text-slate-500">
                    <line x1="0" y1="12" x2="30" y2="12" stroke="currentColor" strokeWidth="2" />
                    <polygon points="30,7 40,12 30,17" fill="currentColor" />
                  </svg>
                </motion.div>
              ))}
            </div>
          )}

          {/* Main Node */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => setSelectedNode(isSelected ? null : step.originalIndex)}
            className={`
              relative px-6 py-4 rounded-xl border cursor-pointer transition-all duration-300
              ${color.bg} ${color.border}
              hover:shadow-lg ${color.glow}
              ${isSelected ? `ring-2 ring-offset-2 ring-offset-slate-900 ${color.border} shadow-xl ${color.glow}` : ''}
              min-w-[200px] max-w-[280px]
            `}
          >
            <p className="text-sm text-slate-200 text-center leading-tight font-medium">{step.label}</p>
            {step.techniques && step.techniques.length > 0 && (
              <div className="absolute -top-1 -right-1">
                <Info className={`w-4 h-4 ${color.text}`} />
              </div>
            )}
          </motion.div>

          {/* Right Side Branch */}
          {branch && branch.some(b => b.side === 'right' || b.side === undefined) && (
            <div className="flex items-center">
              {branch.filter(b => b.side === 'right' || b.side === undefined).map((branchStep, bi) => (
                <motion.div
                  key={bi}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center"
                >
                  {/* Arrow pointing left */}
                  <svg width="40" height="24" className="text-slate-500">
                    <line x1="10" y1="12" x2="40" y2="12" stroke="currentColor" strokeWidth="2" />
                    <polygon points="10,7 0,12 10,17" fill="currentColor" />
                  </svg>
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedNode(selectedNode === branchStep.originalIndex ? null : branchStep.originalIndex);
                    }}
                    className={`
                      px-4 py-3 rounded-xl border cursor-pointer transition-all duration-300
                      ${getNodeColor(branchStep.type).bg} ${getNodeColor(branchStep.type).border}
                      hover:shadow-lg ${getNodeColor(branchStep.type).glow}
                      ${selectedNode === branchStep.originalIndex ? `ring-2 ring-offset-2 ring-offset-slate-900 ${getNodeColor(branchStep.type).border}` : ''}
                      max-w-[180px]
                    `}
                  >
                    <p className="text-sm text-slate-200 text-center leading-tight">{branchStep.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Vertical Connector Arrow */}
        {!isLast && (
          <div className="flex justify-center py-2">
            <svg width="24" height="40" className="text-slate-500">
              <line x1="12" y1="0" x2="12" y2="30" stroke="currentColor" strokeWidth="2" />
              <polygon points="7,30 12,40 17,30" fill="currentColor" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  // Get selected step details
  const selectedStep = selectedNode !== null ? steps[selectedNode] : null;

  return (
    <div className="space-y-6" data-testid="flowchart">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-slate-800">
          <FlaskConical className="w-5 h-5 text-teal-400" />
        </div>
        <h2 className="text-xl font-heading font-bold text-white">Research Methodology Flow</h2>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 p-3 rounded-xl bg-slate-800/30 border border-slate-700/30 text-xs">
        <span className="text-slate-400">Types:</span>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-teal-500/50 border border-teal-500/50" />
          <span className="text-slate-300">Methods</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-200/50 border border-yellow-300/50" />
          <span className="text-slate-300">Results</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-orange-500/50 border border-orange-500/50" />
          <span className="text-slate-300">Limitations</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-violet-500/50 border border-violet-500/50" />
          <span className="text-slate-300">Computational</span>
        </div>
      </div>

      {/* Flowchart Container */}
      <div className="flex gap-6">
        {/* Main Flowchart */}
        <div className="flex-1 p-6 rounded-2xl bg-slate-800/20 border border-slate-700/30">
          <div className="flex flex-col items-center">
            {displaySteps.map((step, index) => (
              <FlowNode
                key={step.originalIndex}
                step={step}
                index={index}
                isLast={index === displaySteps.length - 1}
                hasSideBranch={!!sideBranches[index]}
              />
            ))}
          </div>
        </div>

{/* Details Panel - Desktop */}
      {selectedStep && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden lg:block w-80 shrink-0"
        >
          <div className={`p-5 rounded-2xl border sticky top-4 ${getNodeColor(selectedStep.type).bg} ${getNodeColor(selectedStep.type).border}`}>
            <div className="flex items-center justify-between mb-4">
              <span className={`text-xs font-mono uppercase tracking-wider ${getNodeColor(selectedStep.type).text}`}>
                Step Details
              </span>
              <button onClick={() => setSelectedNode(null)} className="p-1 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <h4 className="text-lg font-heading font-bold text-white mb-3">{selectedStep.label}</h4>
            {selectedStep.description && (
              <p className="text-sm text-slate-300 mb-4 leading-relaxed">{selectedStep.description}</p>
            )}
            {selectedStep.techniques && selectedStep.techniques.length > 0 && (
              <div className="mb-4">
                <h5 className={`text-xs font-mono uppercase tracking-wider ${getNodeColor(selectedStep.type).text} mb-2`}>Key Techniques</h5>
                <ul className="space-y-1.5">
                  {selectedStep.techniques.map((tech, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <ChevronDown className={`w-3 h-3 mt-1 ${getNodeColor(selectedStep.type).text} rotate-[-90deg]`} />
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {selectedStep.micro_summary && (
              <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-700/50">
                <p className="text-xs text-amber-400 mb-1 font-medium">Simple Explanation</p>
                <p className="text-sm text-slate-300 leading-relaxed">{selectedStep.micro_summary}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>

    {/* Mobile Details Panel - Bottom Sheet */}
    {selectedStep && (
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 p-4 bg-slate-900 border-t border-slate-700"
      >
        <div className={`p-4 rounded-2xl border ${getNodeColor(selectedStep.type).bg} ${getNodeColor(selectedStep.type).border}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-xs font-mono uppercase tracking-wider ${getNodeColor(selectedStep.type).text}`}>
              Step Details
            </span>
            <button onClick={() => setSelectedNode(null)} className="p-1 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <h4 className="text-base font-bold text-white mb-2">{selectedStep.label}</h4>
          {selectedStep.description && (
            <p className="text-sm text-slate-300 mb-3 leading-relaxed">{selectedStep.description}</p>
          )}
          {selectedStep.micro_summary && (
            <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-700/50">
              <p className="text-xs text-amber-400 mb-1 font-medium">Simple Explanation</p>
              <p className="text-sm text-slate-300">{selectedStep.micro_summary}</p>
            </div>
          )}
        </div>
      </motion.div>
    )}

    {/* Click hint */}
    {!selectedStep && (
      <p className="text-center text-xs text-slate-500">
        Click on any box to see details about that step
      </p>
    )}
  </div>
  );
};

export default FlowChart;